import { useCallback, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

const ACCEPTED_TYPES = ["image/jpeg", "image/heic", "image/heif", "image/png"];
const ACCEPTED_EXT = ".jpg,.jpeg,.heic,.heif,.png";

const MIN_PHOTOS = 50;
const MAX_PHOTOS = 150;

interface PhotoDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const PhotoDropzone = ({ onFilesSelected, disabled }: PhotoDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<File[]>([]);

  const validate = (files: File[]): string | null => {
    if (files.length < MIN_PHOTOS || files.length > MAX_PHOTOS) {
      return `Please select between ${MIN_PHOTOS} and ${MAX_PHOTOS} photos. You picked ${files.length}.`;
    }
    const bad = files.find((f) => !ACCEPTED_TYPES.includes(f.type));
    if (bad) {
      return `Unsupported file: ${bad.name} (${bad.type || "unknown"}). Use JPEG, HEIC, HEIF, or PNG.`;
    }
    return null;
  };

  const handleFiles = useCallback(
    (incoming: FileList | File[]) => {
      const arr = Array.from(incoming);
      const err = validate(arr);
      if (err) {
        setError(err);
        setSelected([]);
        onFilesSelected([]);
        return;
      }
      setError(null);
      setSelected(arr);
      onFilesSelected(arr);
    },
    [onFilesSelected]
  );

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const totalBytes = selected.reduce((sum, f) => sum + f.size, 0);

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
          disabled
            ? "border-muted bg-muted/20 cursor-not-allowed"
            : dragOver
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/30 hover:border-primary/50"
        }`}
      >
        <p className="text-lg mb-2">
          {selected.length > 0
            ? `${selected.length} photos selected`
            : "Drop photos here, or click to choose"}
        </p>
        <p className="text-sm text-muted-foreground">
          {selected.length > 0
            ? `${formatBytes(totalBytes)} total • JPEG, HEIC, HEIF, PNG`
            : `Aim for 60–80 photos, evenly spaced around the subject (50–150 accepted) • JPEG, HEIC, HEIF, PNG`}
        </p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_EXT}
          multiple
          className="hidden"
          disabled={disabled}
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
      </div>

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3">
          {error}
        </div>
      )}

      {selected.length > 0 && !error && !disabled && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelected([]);
              onFilesSelected([]);
              if (inputRef.current) inputRef.current.value = "";
            }}
          >
            Clear selection
          </Button>
        </div>
      )}
    </div>
  );
};

export default PhotoDropzone;
